import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { ChevronRightIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import { EntityTile } from '@tailwindComponents/EntityTile';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { skuPackDescription } from '../../../utils/sku';
import { variantTotalProducts } from './productGroups';
import { SkuActionsMenu, SkuMenuOption } from './SkuActionsMenu';
import { SkuPackagingPanel } from './SkuPackagingPanel';
import {
  formatSkuDimensions,
  formatSkuWeight,
  hasStockEntries,
  skuCode,
  skuPackagingSummaries,
  skuSubtext,
  skuThumbnailSrc,
} from './skuHelpers';

export interface SkuTableRowProps {
  sku: InventorySKUListing;
  isExpanded: boolean;
  onToggleExpand: (inventorySkuId: string) => void;
  onOpen: (sku: InventorySKUListing) => void;
  isWarehouseUser: boolean;
  menuOptions: SkuMenuOption[];
  isCopied: boolean;
  onCopy: (sku: InventorySKUListing) => void;
  onArchive: (sku: InventorySKUListing) => void;
  nestedUnderProduct?: boolean;
}

export function SkuTableRow({
  sku,
  isExpanded,
  onToggleExpand,
  onOpen,
  isWarehouseUser,
  menuOptions,
  isCopied,
  onCopy,
  onArchive,
  nestedUnderProduct = false,
}: SkuTableRowProps) {
  const hasPackaging = (sku.palletising?.length ?? 0) > 0;
  const subtext = skuSubtext(sku);
  const inlinePackagingSummaries = nestedUnderProduct
    ? skuPackagingSummaries(sku)
    : [];
  const packDescription = skuPackDescription(sku);
  const inStockProducts = variantTotalProducts(sku);
  const archiveDisabled = !sku.isArchived && hasStockEntries(sku);

  const resolveArchiveTitle = () => {
    if (archiveDisabled) return 'Cannot archive a SKU with existing stock.';
    return sku.isArchived ? 'Unarchive SKU' : 'Archive SKU';
  };
  const archiveTitle = resolveArchiveTitle();

  const rowBackground = isExpanded ? 'bg-gray-50/50' : '';

  return (
    <>
      <tr
        className={`cursor-pointer align-middle hover:bg-gray-50 ${rowBackground}`}
        onClick={() => onOpen(sku)}
      >
        <td className='w-10 px-2 py-4'>
          {!nestedUnderProduct && hasPackaging && (
            <button
              type='button'
              aria-expanded={isExpanded}
              aria-label={`${
                isExpanded ? 'Collapse' : 'Expand'
              } packaging for ${sku.name}`}
              className='rounded p-1 transition-colors hover:bg-gray-100'
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(sku.inventorySkuId);
              }}
            >
              <ChevronRightIcon
                className={`h-4 w-4 text-gray-400 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              />
            </button>
          )}
        </td>
        <td className='px-4 py-4'>
          <div
            className={`flex items-center gap-3 ${
              nestedUnderProduct ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''
            }`}
          >
            <EntityTile name={sku.name} imageSrc={skuThumbnailSrc(sku)} />
            <div className='min-w-0'>
              <p
                className={`truncate text-sm text-gray-900 ${
                  nestedUnderProduct ? 'font-medium' : 'font-semibold'
                }`}
              >
                {sku.name}
              </p>
              {subtext && (
                <p className='truncate text-xs text-gray-500'>{subtext}</p>
              )}
              {inlinePackagingSummaries.map((summary) => (
                <p key={summary} className='truncate text-xs text-gray-400'>
                  {summary}
                </p>
              ))}
            </div>
          </div>
        </td>
        <td className='px-4 py-4'>
          <div className='flex items-center gap-1.5 text-sm text-gray-600'>
            <span className='truncate'>{skuCode(sku)}</span>
            <button
              type='button'
              aria-label={`Copy SKU code for ${sku.name}`}
              className='shrink-0'
              onClick={(e) => {
                e.stopPropagation();
                onCopy(sku);
              }}
            >
              {isCopied ? (
                <CheckIcon className='h-4 w-4 text-emerald-600' />
              ) : (
                <ClipboardDocumentIcon className='h-4 w-4 text-gray-400 hover:text-primary' />
              )}
            </button>
          </div>
        </td>
        <td className='px-4 py-4 text-center text-sm text-gray-600'>
          {nestedUnderProduct
            ? packDescription
            : formatSkuDimensions(sku.dimensions)}
        </td>
        <td className='px-4 py-4 text-center text-sm text-gray-600'>
          {nestedUnderProduct
            ? numberToLocale(inStockProducts)
            : formatSkuWeight(sku)}
        </td>
        <td className='px-4 py-4 text-center'>
          {sku.isArchived ? (
            <Badge intent='neutral' label='Archived' />
          ) : (
            <Badge intent='info' label='Active' />
          )}
        </td>
        <td className='px-4 py-4'>
          <div className='flex items-center justify-end gap-2'>
            {isWarehouseUser && (
              <SkuActionsMenu
                id={sku.inventorySkuId}
                options={[
                  ...menuOptions,
                  {
                    label: sku.isArchived ? 'Unarchive' : 'Archive',
                    action: () => onArchive(sku),
                    isDisabled: archiveDisabled,
                    disabledMessage: archiveTitle,
                  },
                ]}
              />
            )}
          </div>
        </td>
      </tr>
      {!nestedUnderProduct && isExpanded && <SkuPackagingPanel sku={sku} />}
    </>
  );
}
